import { Polyline, Postprocessing } from '@editor/services/PolylineExtraction/interfaces'
import { Vector3Like } from 'three'

import { dist, magnitude } from '../../utils/math'

export interface LinkingConfig {
  /**
   * Maximum distance between polyline endpoints to consider them linkable (world units).
   * Larger = more aggressive linking, may connect unrelated segments.
   * Smaller = more conservative, may miss valid connections.
   */
  maxDistance?: number

  /**
   * Maximum angle between polyline directions at connection points (degrees).
   * Helps ensure linked polylines maintain reasonable continuity.
   * Smaller = more strict alignment required.
   * Set to 180 to disable angle checking.
   */
  maxAngle?: number

  /**
   * Maximum number of linking iterations to perform.
   * Each iteration can create new longer polylines that might link in subsequent iterations.
   * Higher = more thorough linking but slower.
   */
  maxIterations?: number
}

/**
 * Links nearby polyline endpoints and filters noise
 */
export class Linking extends Postprocessing {
  protected config: Required<LinkingConfig>

  constructor(config?: LinkingConfig) {
    super()
    this.config = {
      maxDistance: 0.015,
      maxAngle: 90,
      maxIterations: 3,
      ...config,
    }
    this.validateConfig()
  }

  // need to fulfill abstract method
  getName(): string {
    const { maxDistance, maxAngle, maxIterations } = this.config
    return `Linking(maxDistance=${maxDistance * 1000}mm,maxAngle=${maxAngle}deg,maxIterations=${maxIterations})`
  }

  run(polylines: Polyline[]): Polyline[] {
    if (polylines.length === 0) return []

    this.logger?.log(`    🔗 Linking: ${polylines.length} polylines`)

    const { maxIterations } = this.config

    if (polylines.length <= 1) return polylines

    let currentPolylines = [...polylines]
    let iteration = 0

    while (iteration < maxIterations) {
      const linkedPolylines: Polyline[] = []
      const used = new Set<number>()

      for (let i = 0; i < currentPolylines.length; i += 1) {
        if (!used.has(i)) {
          let currentPoly = [...currentPolylines[i]]
          used.add(i)

          // Try to link with other polylines
          let foundLink = true
          while (foundLink) {
            foundLink = false

            for (let j = 0; j < currentPolylines.length; j += 1) {
              if (!used.has(j)) {
                const otherPoly = currentPolylines[j]
                const linkResult = this.tryLinkPolylines(currentPoly, otherPoly)

                if (linkResult) {
                  currentPoly = linkResult
                  used.add(j)
                  foundLink = true
                  break // Start over to check for more links with the new longer polyline
                }
              }
            }
          }

          linkedPolylines.push(currentPoly)
        }
      }

      // If no new links were made, we're done
      if (linkedPolylines.length === currentPolylines.length) break

      currentPolylines = linkedPolylines
      iteration += 1
    }

    this.logger?.log(`    ✅ Linking complete: ${polylines.length} → ${currentPolylines.length} polylines`)

    return currentPolylines
  }

  tryLinkPolylines(poly1: Polyline, poly2: Polyline): Polyline | null {
    if (poly1.length < 2 || poly2.length < 2) return null

    const { maxDistance } = this.config

    const p1Start = poly1[0]
    const p1End = poly1[poly1.length - 1]
    const p2Start = poly2[0]
    const p2End = poly2[poly2.length - 1]

    // Check all possible connections
    const connections = [
      { dist: dist(p1End, p2Start), type: 'end-to-start', result: () => [...poly1, ...poly2.slice(1)] },
      { dist: dist(p1End, p2End), type: 'end-to-end', result: () => [...poly1, ...poly2.slice(0, -1).reverse()] },
      {
        dist: dist(p1Start, p2Start),
        type: 'start-to-start',
        result: () => [...poly1.slice().reverse(), ...poly2.slice(1)],
      },
      { dist: dist(p1Start, p2End), type: 'start-to-end', result: () => [...poly2, ...poly1.slice(1)] },
    ]

    // Find the closest valid connection
    const validConnections = connections.filter((conn) => conn.dist <= maxDistance)
    if (validConnections.length === 0) return null

    const bestConnection = validConnections.reduce((best, conn) => (conn.dist < best.dist ? conn : best))

    // Check angle constraint if specified
    if (!this.isValidLinkAngle(poly1, poly2, bestConnection.type)) return null

    return bestConnection.result()
  }

  isValidLinkAngle(poly1: Polyline, poly2: Polyline, connectionType: string): boolean {
    if (poly1.length < 2 || poly2.length < 2) return true

    const { maxAngle } = this.config

    // Get the appropriate direction vectors based on connection type
    let dir1: Vector3Like
    let dir2: Vector3Like

    switch (connectionType) {
      case 'end-to-start':
        // poly1 end direction + poly2 start direction
        dir1 = {
          x: poly1[poly1.length - 1].x - poly1[poly1.length - 2].x,
          y: poly1[poly1.length - 1].y - poly1[poly1.length - 2].y,
          z: poly1[poly1.length - 1].z - poly1[poly1.length - 2].z,
        }
        dir2 = {
          x: poly2[1].x - poly2[0].x,
          y: poly2[1].y - poly2[0].y,
          z: poly2[1].z - poly2[0].z,
        }
        break
      case 'end-to-end':
        // poly1 end direction + poly2 end direction (reversed)
        dir1 = {
          x: poly1[poly1.length - 1].x - poly1[poly1.length - 2].x,
          y: poly1[poly1.length - 1].y - poly1[poly1.length - 2].y,
          z: poly1[poly1.length - 1].z - poly1[poly1.length - 2].z,
        }
        dir2 = {
          x: poly2[poly2.length - 2].x - poly2[poly2.length - 1].x, // reversed
          y: poly2[poly2.length - 2].y - poly2[poly2.length - 1].y,
          z: poly2[poly2.length - 2].z - poly2[poly2.length - 1].z,
        }
        break
      case 'start-to-start':
        // poly1 start direction (reversed) + poly2 start direction
        dir1 = {
          x: poly1[0].x - poly1[1].x, // reversed
          y: poly1[0].y - poly1[1].y,
          z: poly1[0].z - poly1[1].z,
        }
        dir2 = {
          x: poly2[1].x - poly2[0].x,
          y: poly2[1].y - poly2[0].y,
          z: poly2[1].z - poly2[0].z,
        }
        break
      case 'start-to-end':
        // poly2 end direction + poly1 start direction
        dir1 = {
          x: poly2[poly2.length - 1].x - poly2[poly2.length - 2].x,
          y: poly2[poly2.length - 1].y - poly2[poly2.length - 2].y,
          z: poly2[poly2.length - 1].z - poly2[poly2.length - 2].z,
        }
        dir2 = {
          x: poly1[1].x - poly1[0].x,
          y: poly1[1].y - poly1[0].y,
          z: poly1[1].z - poly1[0].z,
        }
        break
      default:
        return true // Unknown connection type, allow it
    }

    const len1 = magnitude(dir1)
    const len2 = magnitude(dir2)

    // Skip if either direction has zero length
    if (len1 < 1e-10 || len2 < 1e-10) return true

    // Calculate angle between direction vectors
    const dotProduct = (dir1.x * dir2.x + dir1.y * dir2.y + dir1.z * dir2.z) / (len1 * len2)
    const angleRad = Math.acos(Math.max(-1, Math.min(1, dotProduct)))
    const angleDeg = (angleRad * 180) / Math.PI

    // The angle between directions should be small for a smooth connection
    return angleDeg <= maxAngle
  }

  private validateConfig(): void {
    if (this.config.maxDistance <= 0) {
      throw new Error('[Linking] maxDistance must be positive')
    }
    if (this.config.maxAngle && (this.config.maxAngle <= 0 || this.config.maxAngle > 180)) {
      throw new Error('[Linking] maxAngle must be between 0 and 180 degrees')
    }
    if (this.config.maxIterations && this.config.maxIterations < 1) {
      throw new Error('[Linking] maxIterations must be at least 1')
    }
  }
}
